import { useEffect, useState } from "react";
import { getEmailFromIdToken, persistIdToken } from "../../lib/tokenUtils";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setAuthorisedUser,
  setEmployeeTransactionData,
  setIsAuthorised,
  setPartialI9SelectedCard,
} from "../../Store/reducers/AppReducer";
import { clearApiError } from "../../Store/reducers/APIReducer";
import api from "../../utils/apiUtils/apiUtils";
import {
  resolvePrimaryRole,
  resolveAuthorisedUser,
  clearCachedUserProfile,
  setCachedUserProfile,
} from "../../utils/userUtils";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { firebaseSignOut, getFreshIdToken } from "../../lib/firebaseAuth";
import { logout } from "../../Store/reducers/AppReducer";

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user || null);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (authReady && !authUser) {
      navigate("/");
      return;
    }
    if (authUser) {
      checkUserAccess();
    }
  }, [authReady, authUser]);

  const handleLogout = (logoutType) => {
    if (logoutType === "popup" || logoutType === "redirect") {
      firebaseSignOut().finally(() => {
        localStorage.clear();
        sessionStorage.clear();
        clearCachedUserProfile();
        dispatch(logout());
        dispatch(setIsAuthorised(false));
        dispatch(setAuthorisedUser(null));
        navigate("/");
      });
    }
  };

  const checkUserAccess = async () => {
    dispatch(logout());
    dispatch(setIsAuthorised(false));
    dispatch(setAuthorisedUser(null));
    localStorage.removeItem("idToken");
    localStorage.removeItem("idTokenExpiryMs");
    sessionStorage.clear();

    try {
      const accessToken = await getFreshIdToken(false);
      if (!accessToken) {
        throw new Error("Failed to get Firebase token");
      }
      persistIdToken(accessToken);

      const parseJwtPayload = (token) => {
        try {
          const [, payload] = token.split(".");
          if (!payload) return null;
          return JSON.parse(atob(payload));
        } catch {
          return null;
        }
      };

      const maybeBindPhone = async () => {
        const payload = parseJwtPayload(accessToken);
        const pendingPhone = payload?.pending_phone;
        if (!pendingPhone) return;
        if (authUser?.phoneNumber) return;

        try {
          const response = await fetch(
            "https://us-central1-canary-ai-app.cloudfunctions.net/manual_bind_phone",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ phoneNumber: pendingPhone }),
            }
          );

          if (response.ok) {
            await authUser?.reload?.();
            await authUser?.getIdToken?.(true);
          }
        } catch {
        }
      };

      await maybeBindPhone();

      const emailHint =
        authUser?.email || getEmailFromIdToken(accessToken) || "";

      api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      const { profile: authorisedUser } = await resolveAuthorisedUser({
        forceRefresh: true,
        email: emailHint,
      });
      setCachedUserProfile(authorisedUser);

      setCurrentUser(authorisedUser);
      dispatch(setIsAuthorised(true));
      dispatch(setAuthorisedUser(authorisedUser));
      dispatch(clearApiError(null));
    } catch (err) {
      console.error("Profile init error:", err);
      localStorage.clear();
      sessionStorage.clear();
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && location.pathname === "/profile") {
      const primaryRole = resolvePrimaryRole(currentUser?.roles);
      if (currentUser.isSuperAdmin) {
        navigate("/companyadmin");
      } else if ([1, 2].includes(primaryRole?.role)) {
        sessionStorage.setItem("selectedAdminCompanyId", primaryRole?.companyId);
        navigate("/companyadmin");
      } else if ([3].includes(primaryRole?.role)) {
        sessionStorage.setItem("selectedAdminCompanyId", primaryRole?.companyId);
        dispatch(setPartialI9SelectedCard(null));
        dispatch(
          setEmployeeTransactionData({
            userId: currentUser?.id,
            transactionId: primaryRole?.transactionId,
          })
        );
        navigate("/employee/launch");
      } else {
        navigate("/");
      }
    }
  }, [currentUser, dispatch, navigate, location.pathname]);

  if (!authReady || (authReady && !authUser)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <h1 className="text-2xl font-bold mb-4">Loading...</h1>
        <p>Please wait while we verify your authentication...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-screen">
      <h1 className="text-2xl font-bold mb-4">Profile Page showing ......</h1>
      {loading && <p>Loading user data to show.......</p>}
      {!loading && currentUser && (
        <pre className="bg-gray-100 p-4 rounded">
          {JSON.stringify(currentUser, null, 2)}
        </pre>
      )}

      <div className="mt-6 flex gap-6">
        <button
          className="bg-white text-red-500 border-red-500 border rounded-2xl px-4 py-2 cursor-pointer"
          onClick={() => handleLogout("popup")}
        >
          logout Pop
        </button>
        <button
          className="bg-white text-red-500 border-red-500 border rounded-2xl px-4 py-2 cursor-pointer"
          onClick={() => handleLogout("redirect")}
        >
          logout Redirect
        </button>
      </div>
    </div>
  );
};

export default Profile;
